'use client';

import React, { useState, useMemo } from 'react';
import { Search, CheckCircle2, ChevronUp, ChevronDown, ChevronsUpDown, Eye, Trash2, AlertTriangle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { leaveRequests, type LeaveRequest } from '@/lib/mockData';

type SortKey = keyof LeaveRequest | null;
type SortDir = 'asc' | 'desc';

// Seed approved status for display
const initialRows: (LeaveRequest & { status: 'Pending' | 'Approved' })[] = leaveRequests.map((r, i) => ({
  ...r,
  status: i % 2 === 0 ? 'Approved' : 'Pending',
}));

export default function LeaveApprovedTab() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [rows, setRows] = useState(initialRows);
  const [deleteTarget, setDeleteTarget] = useState<(typeof initialRows)[0] | null>(null);
  const [viewTarget, setViewTarget] = useState<(typeof initialRows)[0] | null>(null);

  const filtered = useMemo(() => {
    let data = rows;
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.employeeId.toLowerCase().includes(q) ||
          r.department.toLowerCase().includes(q) ||
          r.typeOfLeave.toLowerCase().includes(q)
      );
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const av = a[sortKey as keyof typeof a] ?? '';
        const bv = b[sortKey as keyof typeof b] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  };

  const handleApprove = (id: string) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: 'Approved' } : r));
    toast('success', 'Leave Approved', 'The leave request has been approved.');
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    toast('success', 'Request removed', `${deleteTarget.employeeId}'s request has been removed.`);
    setDeleteTarget(null);
  };

  const approvedCount = rows.filter((r) => r.status === 'Approved').length;
  const pendingCount = rows.filter((r) => r.status === 'Pending').length;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <CheckCircle2 size={18} className="text-success shrink-0" />
          <div>
            <p className="text-lg font-700 text-foreground tabular-nums">{approvedCount}</p>
            <p className="text-xs text-muted-foreground">Approved</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-warning shrink-0" />
          <div>
            <p className="text-lg font-700 text-foreground tabular-nums">{pendingCount}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3 col-span-2 sm:col-span-1">
          <div className="w-4 h-4 rounded-full bg-primary/20 shrink-0" />
          <div>
            <p className="text-lg font-700 text-foreground tabular-nums">{rows.length}</p>
            <p className="text-xs text-muted-foreground">Total Requests</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by employee, department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-sm border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {([
                  ['employeeId', 'Employee ID'],
                  ['department', 'Department'],
                  ['typeOfLeave', 'Leave Type'],
                  ['startDate', 'Start Date'],
                  ['endDate', 'End Date'],
                  ['reason', 'Reason'],
                  ['status', 'Status'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th
                    key={`th-${key}`}
                    className="text-left px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort(key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground text-right whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <CheckCircle2 size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-600 text-foreground">No leave requests found</p>
                    <p className="text-xs text-muted-foreground mt-1">Try adjusting your search.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((req, idx) => (
                  <tr
                    key={req.id}
                    className={`border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-100 ${
                      idx % 2 === 1 ? 'bg-muted/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-600 text-primary tabular-nums text-xs whitespace-nowrap">
                      {req.employeeId}
                    </td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{req.department}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{req.typeOfLeave}</td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">{req.startDate}</td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">{req.endDate}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-[160px] truncate">
                      {req.reason || <span className="italic text-muted-foreground/50">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-block text-[10px] font-600 px-2 py-0.5 rounded-full ${
                          req.status === 'Approved' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                        }`}
                      >
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setViewTarget(req)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          title="View"
                        >
                          <Eye size={14} />
                        </button>
                        {req.status === 'Pending' && (
                          <button
                            onClick={() => handleApprove(req.id)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-success hover:bg-success/10 transition-colors"
                            title="Approve"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTarget(req)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View modal */}
      {viewTarget && (
        <Modal
          isOpen={!!viewTarget}
          onClose={() => setViewTarget(null)}
          title="Leave Request Details"
          size="md"
        >
          <div className="space-y-3 text-sm">
            {[
              ['Employee ID', viewTarget.employeeId],
              ['Department', viewTarget.department],
              ['Leave Type', viewTarget.typeOfLeave],
              ['Start Date', viewTarget.startDate],
              ['End Date', viewTarget.endDate],
              ['Reason', viewTarget.reason || '—'],
              ['Status', viewTarget.status],
              ['Submitted On', viewTarget.createdAt],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-3">
                <span className="text-muted-foreground w-28 shrink-0">{label}</span>
                <span className="font-600 text-foreground">{value}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-5">
            <button
              onClick={() => setViewTarget(null)}
              className="px-4 py-2 rounded-md bg-muted text-sm font-600 text-foreground hover:bg-muted/80 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Remove Leave Request"
          size="sm"
        >
          <div className="flex items-start gap-3 mb-5">
            <AlertTriangle size={18} className="text-danger mt-0.5 shrink-0" />
            <p className="text-sm text-foreground">
              Are you sure you want to remove the leave request for{' '}
              <span className="font-700">{deleteTarget.employeeId}</span>? This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 rounded-md border border-input text-sm font-600 text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 rounded-md bg-danger text-white text-sm font-600 hover:bg-danger/90 transition-colors"
            >
              Remove
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
